import { Box } from "@material-ui/core";
import { WithT } from "i18next";
import React from "react";
import { withTranslation } from "react-i18next";
import Property from "../../../../../rest/properties/model/Property";
import LabelledText from "../../../../../ui/components/LabelledText";
import EditableField from "../../../../properties/PropertyDetailPage/components/EditableField/EditableField";
import { LARGE_SPACING, SMALL_SPACING } from "../../../../../ui/theme/dimensions";
import ProCheckDetailsPageState from "../../model/ProCheckDetailsPageState";
import axios from "axios";
import { Auth } from "aws-amplify";

interface Props extends WithT {
  resultDetails: ProCheckDetailsPageState;
  property: Property | undefined;
  onBoilerSerialUpdated?: () => void;
}

type EditableItem = "boiler";

interface State {
  openField: EditableItem | undefined;
  loading: boolean;
  error: string | undefined;
}

class ProCheckDetailsTab extends React.Component<Props, State> {
  state: State = {
    openField: undefined,
    loading: false,
    error: undefined,
  };

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (prevState.loading && !this.state.loading && this.state.error === undefined) {
      this.setState({ openField: undefined });
    }
  }

  render() {
    const dateLabel = this.props.t("proCheckDetailsTab.date");
    const timeLabel = this.props.t("proCheckDetailsTab.time");
    const emailLabel = this.props.t("proCheckDetailsTab.email");
    const engineerLabel = this.props.t("proCheckDetailsTab.engineer");
    const reasonLabel = this.props.t("proCheckDetailsTab.reason");
    const boilerLabel = this.props.t("proCheckDetailsTab.boilerNumber");
    const referenceLabel = this.props.t("proCheckDetailsTab.jobReference");
    const addressLabel = this.props.t("proCheckDetailsTab.address");
    const typeLabel = this.props.t("proCheckDetailsTab.type");
    const filterLabel = this.props.t("proCheckDetailsTab.filter");

    const hoverEnabled = this.state.openField === undefined;

    const formatAddress = (property: Property): string => {
      return [
        property.address_line_1,
        property.address_line_2,
        property.address_line_2,
        property.address_city,
        property.address_postcode,
        this.props.t("countryCode." + property.address_country.code),
      ]
        .filter((line) => typeof line === "string")
        .flatMap((line) => (line as string).trim())
        .filter((line) => line.length > 0)
        .join(", ");
    };

    return (
      <Box
        display="flex"
        flexDirection="row"
        alignItems="top"
        paddingX={SMALL_SPACING}
        justifyContent="flex-start"
        maxWidth="60%"
      >
        <Box flexGrow={1}>
          <Box display="flex" flexDirection="row">
            <LabelledText label={dateLabel} text={this.props.resultDetails.date} gutterBottom />
            <Box paddingLeft={LARGE_SPACING} />
            <LabelledText label={timeLabel} text={this.props.resultDetails.time} gutterBottom />
          </Box>

          <LabelledText
            label={emailLabel}
            text={this.props.resultDetails.user.email}
            gutterBottom
          />
          <LabelledText
            label={engineerLabel}
            text={`${this.props.resultDetails.user.first_name} ${this.props.resultDetails.user.last_name}`}
            gutterBottom
          />

          <LabelledText
            label={reasonLabel}
            text={this.props.resultDetails.system_info.reason}
            gutterBottom
          />
          <LabelledText
            label={addressLabel}
            text={this.props.property ? formatAddress(this.props.property) : ""}
            gutterBottom
          />
          <LabelledText
            label={typeLabel}
            text={this.props.resultDetails.system_info.type}
            gutterBottom
          />
          <LabelledText
            label={filterLabel}
            text={this.props.resultDetails.system_info.filter ? "Yes" : "No"}
            gutterBottom
          />

          <EditableField
            label={boilerLabel}
            text={this.props.resultDetails.system_info.serialBoiler}
            isEditing={this.state.openField === "boiler"}
            hoverEnabled={hoverEnabled}
            onEditClicked={() => this.handleEditClicked("boiler")}
            onCancelClicked={this.handleCancelClicked}
            onSaveClicked={this.handleBoilerSaved}
            loading={this.state.loading}
            gutterBottom
          />

          <LabelledText
            label={referenceLabel}
            text={
              this.props.resultDetails.job_reference_id
                ? this.props.resultDetails.job_reference_id
                : "-"
            }
            gutterBottom
          />
        </Box>
      </Box>
    );
  }

  handleEditClicked = (item: EditableItem) => {
    this.setState((prevState) => ({
      openField: prevState.openField || item,
    }));
  };

  handleCancelClicked = () => {
    this.setState({ openField: undefined, error: undefined });
  };

  handleBoilerSaved = async (newValue: string) => {
    if (!this.props.property) {
      console.error("No property available");
      this.setState({ error: "Property data not available" });
      return;
    }

    const organisationId = this.props.property.organisation?.id;
    const propertyId = this.props.property.id;

    if (!organisationId) {
      console.error("Organisation ID is undefined!");
      this.setState({ error: "Organisation ID not available" });
      return;
    }

    this.setState({ loading: true, error: undefined });

    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const baseUrl = process.env.REACT_APP_API_BASE_URL || "";
      const url = `${baseUrl}/organisations/${organisationId}/properties/${propertyId}/devices/procheck/boiler-serial`;

      const payload = {
        wrong_serial: this.props.resultDetails.system_info.serialBoiler || "",
        correct_serial: newValue,
      };

      await axios.put(url, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (this.props.onBoilerSerialUpdated) {
        this.props.onBoilerSerialUpdated();
      }

      this.setState({ loading: false });
    } catch (err: any) {
      console.error("Failed to update boiler serial:", err);
      const errorMessage = err?.response?.data?.message || err.message || "Failed to update";
      this.setState({ loading: false, error: errorMessage });
    }
  };
}

export default withTranslation()(ProCheckDetailsTab);
